import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize, take } from 'rxjs';
import { AuthService } from 'src/app/pages/auth/services/auth.service';
import { DisbursementCertificationService } from 'src/app/services/api/disbursement/disbursement.service';
import { ToastService } from 'src/app/services/toast-service/toast.service';
import {
  DISBURSEMENT_COMPLETED_STATUS_CODES,
  DISBURSEMENT_DOC_LABELS,
  DISBURSEMENT_FINAL_STATUS_CODE,
  DisbursementApprovalTrackEntry,
  DisbursementCertificationModel,
  DisbursementDocType,
  disbursementDocSections,
  isPerTeacherDisbursementDoc,
} from 'src/app/services/models/disbursement/disbursement.model';
import { buildRoundStationSteps, RoundStepEntry } from 'src/app/services/models/disbursement/disbursement-round-steps';
import {
  DisbursementStatusAudience,
  DisbursementStatusDisplay,
  disbursementStatusAudienceFor,
  getDisbursementStatusDisplay,
} from 'src/app/services/models/disbursement/disbursement-status-display';
import { SharedModule } from 'src/shared/shared.module';
import { toBuddhistYear } from 'src/utils/academic-term-options';
import { extractBlobErrorMessage } from 'src/utils/blob-error';
import { RoleID } from 'src/utils/enum/role.enum';

const PREVIEW_ERROR_FALLBACK = 'ยังไม่มีเอกสาร หรือไม่สามารถแสดงเอกสารได้';

// The tester's red strike sits on the SECTION heading above the doc_5 row (the item text
// "หลักฐานการเบิกจ่ายเงินค่าสอนพิเศษ…" is untouched in the mockup), not on doc_2's label.
const REGISTRY_DOC_SECTION_TITLE_OVERRIDES: Partial<Record<DisbursementDocType, string>> = {
  doc_5: 'เอกสารหลักฐานการเบิกจ่าย',
  merged: 'เอกสารทั้งหมด',
};

// The merged bundle only becomes a real ส่งเบิก artefact once เจ้าหน้าที่สารบรรณ has sent the
// round on; before that (and again after a send-back) it is a draft assembled from
// still-editable members. Checked on the round's status, not round_submitted_at — that
// timestamp survives a send-back and would re-expose a reopened round's file.
const ROUND_PRE_SUBMIT_STATUS_CODES = ['draft', 'returned_to_officer'];
const MERGED_LOCKED_NOTE = 'ไฟล์รวมแสดงเมื่อเจ้าหน้าที่สารบรรณส่งให้หัวหน้าสาขาแล้ว';

interface DetailDocRow {
  key: string;
  docType: DisbursementDocType;
  teacherUserId: number | null;
  label: string;
}

interface DetailDocSection {
  title: string;
  docType: DisbursementDocType;
  isEmpty: boolean;
  // Set when the section is deliberately withheld — rendered instead of the doc rows.
  note: string | null;
  rows: DetailDocRow[];
}

interface PreviewPanelState {
  loading: boolean;
  error: boolean;
  errorMessage: string | null;
  url: SafeResourceUrl | null;
}

@Component({
  selector: 'app-disbursement-registry-detail',
  imports: [
    CommonModule,
    SharedModule,
  ],
  templateUrl: './registry-detail.component.html',
  styleUrl: './registry-detail.component.scss',
})
export class DisbursementRegistryDetailComponent implements OnInit, OnDestroy {
  certification = signal<DisbursementCertificationModel | null>(null);
  loading = signal(false);
  notFound = signal(false);

  previewPanels = signal<Partial<Record<string, PreviewPanelState>>>({});
  downloadingDocType = signal<string | null>(null);

  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly authService = inject(AuthService);
  private readonly disbursementService = inject(DisbursementCertificationService);
  private readonly toast = inject(ToastService);
  // Keeps raw URLs to revoke them; sanitized SafeResourceUrl lives in previewPanels.
  private readonly rawPreviewUrls = new Map<string, string>();

  ngOnInit(): void {
    const id = Number(this.route.snapshot.paramMap.get('id'));
    if (!Number.isFinite(id) || id <= 0) {
      void this.router.navigate(['/disbursement-management/registry']);
      return;
    }

    this.loadCertification(id);
  }

  ngOnDestroy(): void {
    this.revokeAllPreviews();
  }

  // --- Header / derived state ---

  get isDocumentOfficerRole(): boolean {
    return this.authService.getActiveRoleId() === RoleID.DOCUMENT_OFFICER;
  }

  get isAdminRole(): boolean {
    return this.authService.getActiveRoleId() === RoleID.ADMIN;
  }

  // A getter, not a cached field: switching role must re-render the badges.
  get statusAudience(): DisbursementStatusAudience {
    return disbursementStatusAudienceFor(this.authService.getActiveRoleId());
  }

  teacherName(): string {
    const cert = this.certification();
    if (cert == null) {
      return '-';
    }

    if (cert.user != null) {
      return cert.user.full_name;
    }

    if (cert.subject != null) {
      return `${cert.subject.code} ${cert.subject.name}`.trim();
    }

    return '-';
  }

  isInternational(): boolean {
    return this.certification()?.path === 'international';
  }

  teacherListLabel(): string {
    const teachers = this.certification()?.teachers ?? [];
    const names = teachers
      .map((teacher) => teacher.full_name)
      .filter((name): name is string => name != null && name !== '');
    return names.length > 0 ? names.join(', ') : '-';
  }

  departmentName(): string {
    const dept = this.certification()?.department;
    return dept?.name_th ?? dept?.name ?? dept?.name_en ?? '-';
  }

  academicTermLabel(): string {
    const term = this.certification()?.academic_term;
    if (term == null) {
      return '-';
    }

    return `${term.name}/${toBuddhistYear(term.year)}`;
  }

  statusDisplay(): DisbursementStatusDisplay {
    if (this.isExpired()) {
      return { tone: 'special_revision', label: 'ไม่สำเร็จ (หมดเขต)' };
    }

    return getDisbursementStatusDisplay(this.certification()?.status_code, this.statusAudience);
  }

  isExpired(): boolean {
    const cert = this.certification();
    if (cert == null) {
      return false;
    }

    // v2.0: only the round's completion finishes a cert — an exported ZIP is no longer a signal.
    return cert.window_closed === true && cert.status_code !== 'completed';
  }

  approvalTrack(): DisbursementApprovalTrackEntry[] {
    return this.certification()?.approval_track ?? [];
  }

  stationPersonNames(entry: DisbursementApprovalTrackEntry): string {
    return entry.person_names.length > 0 ? entry.person_names.join(', ') : 'ยังไม่ได้กำหนด';
  }

  // Card VISIBILITY only, not a download gate (see downloadsUnlocked()). ADR-CY: สารบรรณ/admin
  // read documents at ANY status — context/decisions/DECISION_DisbursementV20.md
  documentsVisible(): boolean {
    if (this.isDocumentOfficerRole || this.isAdminRole) {
      return true;
    }

    const status = this.certification()?.status_code;
    return status != null && DISBURSEMENT_COMPLETED_STATUS_CODES.includes(status);
  }

  // Download gate — only once the round itself finishes. Preview stays available under
  // documentsVisible() the whole time; only the "ดาวน์โหลด" button is held back by this.
  downloadsUnlocked(): boolean {
    return this.certification()?.status_code === DISBURSEMENT_FINAL_STATUS_CODE;
  }

  // --- v2.0 round section (sign dates) — shown once เจ้าหน้าที่สารบรรณ has put this cert
  // in a รอบส่งเบิก; absent (not just empty) on the payload until then. 86eytehq2 dropped
  // the รอบที่ N subtitle and the section's own status badge — the stepper below carries
  // the round's state, and the cert's status is already the header-card badge. ---

  hasRoundSection(): boolean {
    return this.certification()?.round_status_code != null;
  }

  roundSteps(): RoundStepEntry[] {
    const cert = this.certification();
    if (cert == null || !this.hasRoundSection()) {
      return [];
    }

    return buildRoundStationSteps({
      status_code: cert.round_status_code,
      submitted_at: cert.round_submitted_at,
      finance_signed_at: cert.round_finance_signed_at,
      head_signed_at: cert.round_head_signed_at,
      vice_dean_signed_at: cert.round_vice_dean_signed_at,
    });
  }

  // --- Document sections ---

  docLabel(docType: string): string {
    return DISBURSEMENT_DOC_LABELS[docType] ?? docType;
  }

  sectionTitle(spec: { title: string; docType: DisbursementDocType }): string {
    return REGISTRY_DOC_SECTION_TITLE_OVERRIDES[spec.docType] ?? spec.title;
  }

  // เจ้าหน้าที่สารบรรณ cuts a รอบส่งเบิก as a draft and may still edit its members, so the
  // merged bundle stays hidden behind a note until the round is actually sent onward.
  mergedDocumentVisible(): boolean {
    const roundStatus = this.certification()?.round_status_code;
    return roundStatus != null && !ROUND_PRE_SUBMIT_STATUS_CODES.includes(roundStatus);
  }

  documentSections(): DetailDocSection[] {
    const cert = this.certification();
    if (cert == null) {
      return [];
    }

    return disbursementDocSections(cert.path).map((spec) => {
      const withheld = spec.docType === 'merged' && !this.mergedDocumentVisible();

      return {
        title: this.sectionTitle(spec),
        docType: spec.docType,
        isEmpty: this.isEmptyDoc1Section(spec.docType),
        note: withheld ? MERGED_LOCKED_NOTE : null,
        rows: withheld ? [] : this.docRowsFor(cert, spec.docType),
      };
    });
  }

  private docRowsFor(cert: DisbursementCertificationModel, docType: DisbursementDocType): DetailDocRow[] {
    if (cert.path !== 'international' || !isPerTeacherDisbursementDoc(docType)) {
      return [{ key: docType, docType, teacherUserId: null, label: this.docLabel(docType) }];
    }

    const teachers = cert.teachers ?? [];
    if (teachers.length === 0) {
      return [{ key: docType, docType, teacherUserId: null, label: this.docLabel(docType) }];
    }

    return teachers.map((teacher) => ({
      key: `${docType}:${teacher.user_id}`,
      docType,
      teacherUserId: teacher.user_id,
      label: `${this.docLabel(docType)} — ${teacher.full_name ?? '-'}`,
    }));
  }

  isEmptyDoc1Section(docType: string): boolean {
    return docType === 'doc_1' && this.certification()?.overload_memo?.has_pdf !== true;
  }

  // --- Inline preview ---

  isPreviewExpanded(key: string): boolean {
    return this.previewPanels()[key] != null;
  }

  isPreviewLoading(key: string): boolean {
    return this.previewPanels()[key]?.loading ?? false;
  }

  previewHasError(key: string): boolean {
    return this.previewPanels()[key]?.error ?? false;
  }

  previewErrorMessage(key: string): string | null {
    return this.previewPanels()[key]?.errorMessage ?? null;
  }

  previewUrl(key: string): SafeResourceUrl | null {
    return this.previewPanels()[key]?.url ?? null;
  }

  onTogglePreview(docRow: DetailDocRow): void {
    if (this.isPreviewExpanded(docRow.key)) {
      this.collapsePreview(docRow.key);
      return;
    }

    this.expandPreview(docRow);
  }

  // --- Download ---

  isDownloadingDoc(key: string): boolean {
    return this.downloadingDocType() === key;
  }

  onDownloadDocument(docRow: DetailDocRow): void {
    const cert = this.certification();
    if (cert == null || this.downloadingDocType() != null) {
      return;
    }

    this.downloadingDocType.set(docRow.key);
    this.disbursementService.downloadDocument(cert.id, docRow.docType, docRow.teacherUserId).pipe(
      take(1),
      finalize(() => this.downloadingDocType.set(null)),
    ).subscribe({
      next: (blob) => this.downloadBlob(blob, this.docFileName(docRow.label)),
      error: (err) => {
        console.error(err);
        // responseType 'blob' wraps the JSON body in a Blob, so the plain object reader below
        // never sees it — the ไฟล์รวม 422 ("กำลังจัดทำเอกสารของรอบนี้…") is the message that matters.
        void extractBlobErrorMessage(err, this.extractErrorMessage(err)).then((message) => {
          this.toast.toastError(message);
        });
      },
    });
  }

  onBack(): void {
    void this.router.navigate(['/disbursement-management/registry']);
  }

  formatDate(value?: string | null): string {
    if (value == null || value === '') {
      return '-';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return [
      String(date.getDate()).padStart(2, '0'),
      String(date.getMonth() + 1).padStart(2, '0'),
      String(toBuddhistYear(date.getFullYear())),
    ].join('/');
  }

  private loadCertification(id: number): void {
    this.loading.set(true);
    this.disbursementService.getCertification(id).pipe(
      take(1),
      finalize(() => this.loading.set(false)),
    ).subscribe({
      next: (cert) => this.certification.set(cert),
      error: (err) => {
        console.error(err);
        this.notFound.set(true);
      },
    });
  }

  private expandPreview(docRow: DetailDocRow): void {
    const cert = this.certification();
    if (cert == null) {
      return;
    }

    this.patchPreviewPanels(docRow.key, { loading: true, error: false, errorMessage: null, url: null });
    this.disbursementService.previewDocument(cert.id, docRow.docType, docRow.teacherUserId).pipe(take(1)).subscribe({
      next: (blob) => {
        const rawUrl = window.URL.createObjectURL(blob);
        this.rawPreviewUrls.set(docRow.key, rawUrl);
        this.patchPreviewPanels(docRow.key, {
          loading: false,
          error: false,
          errorMessage: null,
          url: this.sanitizer.bypassSecurityTrustResourceUrl(`${rawUrl}#toolbar=0&navpanes=0`),
        });
      },
      error: (err) => {
        console.error(err);
        void extractBlobErrorMessage(err, PREVIEW_ERROR_FALLBACK).then((errorMessage) => {
          this.patchPreviewPanels(docRow.key, { loading: false, error: true, errorMessage, url: null });
        });
      },
    });
  }

  private collapsePreview(key: string): void {
    this.revokePreview(key);
    this.previewPanels.update((panels) => {
      const next = { ...panels };
      delete next[key];
      return next;
    });
  }

  private patchPreviewPanels(key: string, state: PreviewPanelState): void {
    this.previewPanels.update((panels) => ({ ...panels, [key]: state }));
  }

  private revokePreview(key: string): void {
    const rawUrl = this.rawPreviewUrls.get(key);
    if (rawUrl != null) {
      window.URL.revokeObjectURL(rawUrl);
      this.rawPreviewUrls.delete(key);
    }
  }

  private revokeAllPreviews(): void {
    this.rawPreviewUrls.forEach((rawUrl) => window.URL.revokeObjectURL(rawUrl));
    this.rawPreviewUrls.clear();
    this.previewPanels.set({});
  }

  // Mirrors the on-screen row label so the downloaded file's name matches what the user
  // sees, instead of the internal `${docType}:${teacherUserId}` key.
  private docFileName(label: string): string {
    const sanitized = label.replace(/[\\/:*?"<>|]/g, '-').replace(/\s+/g, ' ').trim();
    return `${sanitized}.pdf`;
  }

  private downloadBlob(blob: Blob, fileName: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = fileName;
    link.click();
    window.URL.revokeObjectURL(url);
  }

  private extractErrorMessage(err: unknown): string {
    if (err !== null && typeof err === 'object' && 'error' in err) {
      const error = (err as { error?: unknown }).error;
      if (error !== null && typeof error === 'object' && 'errors' in error) {
        const errors = (error as { errors?: unknown }).errors;
        if (Array.isArray(errors) && errors.length > 0) {
          return String(errors[0]);
        }
        if (typeof errors === 'string') {
          return errors;
        }
      }
    }

    return 'ไม่สามารถดำเนินการได้';
  }
}
